/**
 * One override row: several campaigns sharing the same threshold — a pinned
 * number, or (via the ⇄ toggle, mirroring the condition row) their own
 * COMPARISON against another metric over its own window.
 */

import type { FC } from 'react';
import { useCallback } from 'react';
import { ArrowLeftRight, Hash, Trash2 } from 'lucide-react';

import { Button, Input } from '@/components/ui';
import { OverrideCompareControls } from '@/features/tag-rule-compare/controls/OverrideCompareControls';
import { cn } from '@/lib/utils';

import { useSyncedThresholdInput } from '../use-synced-threshold-input';
import { CampaignMultiPicker } from './CampaignMultiPicker';
import {
  campaignKey,
  type CampaignOption,
  type CampaignThresholdUIGroup,
} from './campaign-threshold-parts';

interface CampaignOverrideRowProps {
  group: CampaignThresholdUIGroup;
  options: CampaignOption[];
  maxKeys: number;
  isLoading: boolean;
  onPatch: (
    id: string,
    patch: Partial<
      Pick<CampaignThresholdUIGroup, 'campaigns' | 'value' | 'compareTo'>
    >,
  ) => void;
  onRemove: (id: string) => void;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}

export const CampaignOverrideRow: FC<CampaignOverrideRowProps> = ({
  group,
  options,
  maxKeys,
  isLoading,
  onPatch,
  onRemove,
  fields,
}) => {
  const commitValue = useCallback(
    (n: number) => onPatch(group.id, { value: n }),
    [group.id, onPatch],
  );
  const threshold = useSyncedThresholdInput(group.value, commitValue);
  const compare = group.compareTo;

  const picker = (
    <CampaignMultiPicker
      options={options}
      selected={group.campaigns}
      maxKeys={maxKeys}
      isLoading={isLoading}
      onChange={(next) => {
        const seen = new Set<string>();
        const unique = next.filter((c) => {
          const k = campaignKey(c.provider, c.campaignId);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        onPatch(group.id, { campaigns: unique });
      }}
    />
  );
  const removeBtn = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      onClick={() => onRemove(group.id)}
      aria-label="Remove campaign override"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );

  if (compare) {
    return (
      <div className="space-y-2 border-b border-border/40 px-4 py-3 last:border-b-0 hover:bg-background/40">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">{picker}</div>
          <button
            type="button"
            onClick={() => onPatch(group.id, { compareTo: undefined })}
            title="Pin a fixed value for these campaigns instead"
            aria-label="Use a fixed value for this override"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Hash className="h-4 w-4" />
          </button>
          {removeBtn}
        </div>
        <OverrideCompareControls
          ct={compare}
          onChange={(ct) => onPatch(group.id, { compareTo: ct })}
          fields={fields}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-2 border-b border-border/40 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_2.5rem_2.5rem] sm:items-center sm:gap-3 sm:py-2',
        'last:border-b-0 hover:bg-background/40',
      )}
    >
      <div className="min-w-0">
        <span className="mb-1 block text-[11px] font-medium text-muted-foreground sm:sr-only">
          Campaigns
        </span>
        {picker}
      </div>
      <div className="min-w-0 sm:text-right">
        <span className="mb-1 block text-[11px] font-medium text-muted-foreground sm:sr-only">
          Value
        </span>
        <Input
          type="number"
          value={threshold.display}
          onChange={(e) => threshold.onChange(e.target.value)}
          onBlur={threshold.onBlur}
          onKeyDown={threshold.onKeyDown}
          className="h-9 w-full font-medium tabular-nums sm:ml-auto sm:max-w-[5.5rem]"
          aria-label="Value for selected campaigns"
        />
      </div>
      <div className="flex justify-end sm:justify-center">
        <button
          type="button"
          onClick={() =>
            onPatch(group.id, {
              compareTo: { field: fields?.[0]?.key ?? 'profit' },
            })
          }
          title="Compare these campaigns against another metric instead of a fixed value"
          aria-label="Compare this override against a metric"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
        >
          <ArrowLeftRight className="h-4 w-4" />
        </button>
      </div>
      <div className="flex justify-end sm:justify-center sm:pt-0">{removeBtn}</div>
    </div>
  );
};
